import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { ProductCatalogTable, ProductSearch, AddProductModal, EditProductModal } from './components';
import { ProductItem } from './productCatalogTypes';
import { useSkusStore } from '../../../store/skusStore';
import { Sku } from '../../../services/skusApi';
import styles from './AdminProductCatalog.module.css';

function adapt(s: Sku): ProductItem {
  // Tier pricing isn't yet sourced per-tier; use list_price as placeholder for all tiers.
  const lp = s.list_price ?? s.unit_cost;
  return {
    id: s.id,
    sku: s.sku_code,
    barcode: s.barcode,
    name: s.product_name,
    ageRestriction: s.age_restriction ?? undefined,
    category: s.category_name || '—',
    cost: s.unit_cost,
    tierA: lp,
    tierB: lp * 0.99,
    tierC: lp * 0.97,
    tierD: lp * 0.95,
    stock: s.total_stock,
    margin: s.margin_percent,
    imageUrl: s.image_url,
  };
}

const AdminProductCatalog: React.FC = () => {
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  const items = useSkusStore((s) => s.items);
  const loading = useSkusStore((s) => s.loading);
  const error = useSkusStore((s) => s.error);
  const fetchList = useSkusStore((s) => s.fetchList);
  const remove = useSkusStore((s) => s.remove);
  const [editing, setEditing] = useState<Sku | null>(null);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleEdit = (id: number) => {
    const sku = items.find((x) => x.id === id);
    if (sku) setEditing(sku);
  };

  const handleDelete = async (id: number) => {
    const sku = items.find((x) => x.id === id);
    if (!sku) return;
    if (!window.confirm(`Delete product "${sku.product_name}" (${sku.sku_code})? This cannot be undone.`)) return;
    try {
      await remove(id);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to delete product');
    }
  };

  const adapted = useMemo<ProductItem[]>(() => items.map(adapt), [items]);

  const filtered = useMemo(() => {
    if (!search.trim()) return adapted;
    const q = search.toLowerCase();
    return adapted.filter(
      (p) =>
        p.sku.toLowerCase().includes(q) ||
        (p.barcode || '').toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
    );
  }, [adapted, search]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Product Catalog</h2>
          <p className={styles.subtitle}>Manage your product inventory and pricing</p>
        </div>
        <button className={styles.addBtn} onClick={() => setShowAddModal(true)}>
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
          Add Product
        </button>
      </div>

      <ProductSearch value={search} onChange={setSearch} />

      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && filtered.length === 0}
        emptyMessage="No products in the catalog yet"
        onRetry={() => fetchList()}
      >
        <ProductCatalogTable items={filtered} onEdit={handleEdit} onDelete={handleDelete} />
      </AsyncBoundary>

      <AddProductModal open={showAddModal} onClose={() => setShowAddModal(false)} />
      <EditProductModal open={!!editing} product={editing} onClose={() => setEditing(null)} />
    </div>
  );
};

export const AdminProductCatalogPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin', 'Customer']}>
    <AdminProductCatalog />
  </ProtectedRoute>
);

export { AdminProductCatalog };
export default AdminProductCatalog;
